import { supabase } from '../lib/supabase';
import { PushNotificationService } from './pushNotificationService';

export interface SearchParams {
  name?: string;
  phone?: string;
  country?: string;
  city?: string;
}

export interface SingleSearchParams {
  country?: string;
  city?: string;
  ageRange?: string;
  gender?: string;
  sexualOrientation?: string;
}

export interface SearchResult {
  relation_id: string;
  photo: string;
  name: string;
  country: string;
  city: string;
  status: string;
  confirmed: boolean;
  user_id: string;
  creator_name: string;
  creator_photo: string;
  creator_link: string | null;
  match_percentage: number;
  created_at: string;
  comment: string | null;
  search_count?: number; // Nouveau champ pour le compteur de recherches
}

export interface SingleSearchResult {
  id: string;
  full_name: string;
  avatar_url: string;
  country: string;
  city: string;
  gender: string;
  age: string;
  relation_type: string;
  custom_link_url: string | null;
  short_term_intentions: string[] | null;
  long_term_intentions: string[] | null;
  money_amount: string | null;
  money_flow: string | null;
  photos: string[] | null;
  description: string | null;
}

export interface Crush {
  id: string;
  user_id: string;
  crush_id: string;
  created_at: string;
  expires_at: string;
  profile?: SingleSearchResult;
  is_annonce?: boolean; // Indiquer si c'est une annonce ou un profil normal
}

export async function searchRelations(params: SearchParams): Promise<SearchResult[]> {
  try {
    // Normaliser les paramètres de recherche
    const normalizedName = params.name ? normalizeString(params.name) : null;
    const normalizedCountry = params.country ? normalizeString(params.country) : null;
    const normalizedCity = params.city ? normalizeString(params.city) : null;

    const { data, error } = await supabase
      .rpc('search_relations', {
        search_name: params.name?.trim() || null,
        search_phone: params.phone?.trim() || null,
        search_country: params.country?.trim() || null,
        search_city: params.city?.trim() || null
      })
      .throwOnError();

    if (error) {
      console.error('Error searching relations:', error);
      return [];
    }

    // Incrémenter le compteur de recherches pour chaque relation trouvée
    if (data && data.length > 0) {
      await Promise.all(
        data.map(async (result: { relation_id: string }) => {
          try {
            await supabase.rpc('increment_search_count', {
              relation_id_param: result.relation_id
            });
          } catch (incrementError) {
            console.error('Erreur lors de l\'incrémentation du compteur:', incrementError);
          }
        })
      );
    }

    // Récupérer les informations actuelles des créateurs pour tous les résultats
    if (data && data.length > 0) {
      const creatorIds = [...new Set(data.map((item: { user_id: string }) => item.user_id))].filter(Boolean);
      
      if (creatorIds.length > 0) {
        const { data: creatorProfiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url, custom_link_url')
          .in('id', creatorIds);
        
        if (!profilesError && creatorProfiles) {
          const profilesMap = creatorProfiles.reduce((map, profile) => {
            map[profile.id] = profile;
            return map;
          }, {} as Record<string, any>);
          
          data.forEach((result: { user_id: string; creator_name: string; creator_photo: string; creator_link: string | null }) => {
            const profile = profilesMap[result.user_id];
            if (profile) {
              result.creator_name = profile.full_name;
              result.creator_photo = profile.avatar_url;
              result.creator_link = profile.custom_link_url;
            }
          });
        }
      }
    }

    // Filtrer et formater les résultats avec la correspondance approximative
    const formattedResults = data ? data.reduce((acc: SearchResult[], result: any) => {
      // Filtrer les résultats sans nom ou sans photo
      if (!result.name || !result.photo) {
        return acc;
      }

      // Normaliser les valeurs du résultat pour la comparaison
      const resultName = normalizeString(result.name);
      const resultCountry = result.country ? normalizeString(result.country) : '';
      const resultCity = result.city ? normalizeString(result.city) : '';

      // Vérifier la correspondance avec les critères normalisés
      const nameMatch = !normalizedName || resultName.includes(normalizedName);
      const countryMatch = !normalizedCountry || resultCountry.includes(normalizedCountry);
      const cityMatch = !normalizedCity || resultCity.includes(normalizedCity);

      // Si tous les critères spécifiés correspondent
      if (nameMatch && countryMatch && cityMatch) {
        const formattedResult = {
          ...result,
          match_percentage: typeof result.match_percentage === 'string' 
            ? parseFloat(result.match_percentage) 
            : Number(result.match_percentage) || 0,
          creator_link: result.creator_link || null,
          country: result.country || '',
          city: result.city || '',
          comment: result.comment || null,
          created_at: result.created_at || new Date().toISOString()
        };
        
        acc.push(formattedResult);

        // Envoyer une notification si le score de correspondance est élevé
        if (formattedResult.match_percentage > 80) {
          setTimeout(() => {
            PushNotificationService.notifyRelationSearched(
              result.user_id,
              result.name
            ).catch(console.error);
          }, 0);
        }
      }

      return acc;
    }, []) : [];

    return formattedResults;
  } catch (error) {
    console.error('Error in searchRelations:', error);
    return [];
  }
}

// Fonction utilitaire pour normaliser les chaînes de caractères
function normalizeString(str: string): string {
  return str
    .toLowerCase()
    // Remplacer les caractères accentués
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Supprimer les caractères spéciaux et la ponctuation
    .replace(/[^a-z0-9\s]/g, '')
    // Remplacer les espaces multiples par un seul espace
    .replace(/\s+/g, ' ')
    .trim();
}

export async function searchSingles(params: SingleSearchParams): Promise<SingleSearchResult[]> {
  try {
    // Normaliser les paramètres de recherche
    const normalizedCountry = params.country ? normalizeString(params.country) : null;
    const normalizedCity = params.city ? normalizeString(params.city) : null;

    // 1. Rechercher les profils d'utilisateurs comme avant
    const { data: profilesData, error: profilesError } = await supabase
      .rpc('search_singles', {
        search_country: params.country?.trim() || null,
        search_city: params.city?.trim() || null,
        search_age_range: params.ageRange || null,
        search_gender: params.gender || null,
        search_sexual_orientation: params.sexualOrientation || null
      });

    if (profilesError) {
      console.error('Error searching singles with RPC:', profilesError);
    }

    // Log pour le debugging
    console.log(`Found ${profilesData?.length || 0} singles matching criteria:`, params);
    
    // 2. Rechercher les annonces en utilisant la fonction RPC
    const { data: annoncesData, error: annoncesError } = await supabase
      .rpc('search_profil_annonces', {
        search_country: params.country?.trim() || null,
        search_city: params.city?.trim() || null,
        search_age_range: params.ageRange || null,
        search_gender: params.gender || null,
        search_sexual_orientation: params.sexualOrientation || null
      });

    if (annoncesError) {
      console.error('Error searching annonces with RPC:', annoncesError);
      // Continuer avec les données de profils même si la recherche d'annonces échoue
    }

    console.log(`Found ${annoncesData?.length || 0} annonces matching criteria`);

    // 3. Convertir les annonces en format SingleSearchResult
    const annoncesResults: SingleSearchResult[] = (annoncesData || []).map((annonce: any) => ({
      id: annonce.id,
      full_name: annonce.nom || 'Utilisateur',
      avatar_url: annonce.photos_urls && annonce.photos_urls.length > 0 ? annonce.photos_urls[0] : '',
      country: annonce.pays || '',
      city: annonce.ville || '',
      gender: annonce.sexe === 'Homme' ? 'homme' : 'femme',
      age: String(annonce.age) || '',
      relation_type: annonce.type_relation === 'court terme' ? 'court' : 'long',
      custom_link_url: annonce.contact || null,
      short_term_intentions: annonce.type_relation === 'court terme' ? 
        mapProfilIntentionsToShortTerm(annonce.intentions_court_terme || []) : null,
      long_term_intentions: annonce.type_relation === 'long terme' ? 
        mapProfilIntentionsToLongTerm(annonce.intentions_long_terme || []) : null,
      money_amount: annonce.prix ? String(annonce.prix) : null,
      money_flow: 'give', // Par défaut
      photos: annonce.photos_urls || [],
      description: annonce.description || null
    }));

    // 4. Combiner les résultats de profils et d'annonces
    const allResults = [...(profilesData || []), ...annoncesResults];
    
    // Vérifier et formater les résultats
    if (allResults.length === 0) {
      return [];
    }

    // Filtrer et formater les résultats avec la correspondance approximative
    return allResults
      .filter((profile: any) => {
        // Si aucun filtre de pays/ville n'est appliqué, garder le profil
        if (!normalizedCountry && !normalizedCity) return true;

        // Normaliser les valeurs du profil
        const profileCountry = profile.country ? normalizeString(profile.country) : '';
        const profileCity = profile.city ? normalizeString(profile.city) : '';

        // Vérifier la correspondance du pays si spécifié
        const countryMatch = !normalizedCountry || profileCountry.includes(normalizedCountry);
        
        // Vérifier la correspondance de la ville si spécifiée
        const cityMatch = !normalizedCity || profileCity.includes(normalizedCity);

        // Le profil doit correspondre aux deux critères si les deux sont spécifiés
        return countryMatch && cityMatch;
      })
      .map((profile: any) => {
        return {
          id: profile.id,
          full_name: profile.full_name || 'Utilisateur',
          avatar_url: profile.avatar_url || '',
          country: profile.country || '',
          city: profile.city || '',
          gender: profile.gender || '',
          age: profile.age || '',
          relation_type: profile.relation_type || '',
          custom_link_url: profile.custom_link_url,
          short_term_intentions: profile.short_term_intentions,
          long_term_intentions: profile.long_term_intentions,
          money_amount: profile.money_amount || null,
          money_flow: profile.money_flow || 'give',
          photos: profile.photos || [],
          description: profile.description || null
        };
      });

  } catch (error) {
    console.error('Error in searchSingles:', error);
    
    // En cas d'erreur, on essaie la méthode de secours
    try {
      console.log('Trying fallback method for single search...');
      
      // Requête de base pour récupérer les profils visibles
      let query = supabase
        .from('profiles')
        .select('id, full_name, avatar_url, country, city, gender, age, relation_type, custom_link_url, short_term_intentions, long_term_intentions, money_amount, money_flow, photos, description')
        .eq('is_visible', true);

      // Filtrer par sexe si spécifié - adapté pour les valeurs 'homme' et 'femme'
      if (params.gender) {
        query = query.eq('gender', params.gender);
      }

      // Filtrer par pays si spécifié avec correspondance approximative
      if (params.country && params.country.trim() !== '') {
        const normalizedCountry = normalizeString(params.country);
        query = query.or(`country.ilike.%${params.country.trim()}%,country.ilike.%${normalizedCountry}%`);
      }

      // Filtrer par ville si spécifiée avec correspondance approximative
      if (params.city && params.city.trim() !== '') {
        const normalizedCity = normalizeString(params.city);
        query = query.or(`city.ilike.%${params.city.trim()}%,city.ilike.%${normalizedCity}%`);
      }

      // Filtrer par tranche d'âge si spécifiée
      if (params.ageRange) {
        if (params.ageRange === '56+') {
          // Pour une tranche comme "56+"
          query = query.gte('age', '56');
        } else {
          const [minAge, maxAge] = params.ageRange.split('-');
          if (minAge && maxAge) {
            // Pour une tranche comme "18-25"
            query = query.gte('age', minAge).lte('age', maxAge);
          }
        }
      }

      // Filtrer par orientation sexuelle si spécifiée
      if (params.sexualOrientation) {
        query = query.eq('sexual_orientation', params.sexualOrientation);
      }

      // Exécuter la requête
      const { data: profilesData, error: profilesError } = await query;

      if (profilesError) {
        console.error('Error with fallback search for profiles:', profilesError);
        return [];
      }

      // Recherche de secours pour les annonces
      let annonceQuery = supabase
        .from('profil_annonce')
        .select('*')
        .eq('active', true);  // Ne prendre que les annonces actives

      // Filtrer par sexe si spécifié
      if (params.gender) {
        const genreAnnonce = params.gender === 'homme' ? 'Homme' : 'Femme';
        annonceQuery = annonceQuery.eq('sexe', genreAnnonce);
      }

      // Filtrer par pays/ville si spécifiés
      if (params.country && params.country.trim()) {
        annonceQuery = annonceQuery.ilike('pays', `%${params.country.trim()}%`);
      }
      
      if (params.city && params.city.trim()) {
        annonceQuery = annonceQuery.ilike('ville', `%${params.city.trim()}%`);
      }

      // Filtrer par tranche d'âge si spécifiée
      if (params.ageRange) {
        if (params.ageRange === '56+') {
          annonceQuery = annonceQuery.gte('age', 56);
        } else {
          const [minAge, maxAge] = params.ageRange.split('-').map(Number);
          if (minAge && maxAge) {
            annonceQuery = annonceQuery.gte('age', minAge).lte('age', maxAge);
          }
        }
      }

      // Filtrer par orientation sexuelle si spécifiée
      if (params.sexualOrientation) {
        annonceQuery = annonceQuery.eq('orientation_sexuelle', params.sexualOrientation);
      }

      const { data: annoncesData, error: annoncesError } = await annonceQuery;

      if (annoncesError) {
        console.error('Error with fallback search for annonces:', annoncesError);
        // Continuer avec les données de profils même si la recherche d'annonces échoue
      }

      // Formater les résultats de profils
      const mappedProfileResults = (profilesData || []).map(profile => {
        return {
          id: profile.id,
          full_name: profile.full_name || 'Utilisateur',
          avatar_url: profile.avatar_url || '',
          country: profile.country || '',
          city: profile.city || '',
          gender: profile.gender || '',
          age: profile.age || '',
          relation_type: profile.relation_type || '',
          custom_link_url: profile.custom_link_url,
          short_term_intentions: profile.short_term_intentions,
          long_term_intentions: profile.long_term_intentions,
          money_amount: profile.money_amount || null,
          money_flow: profile.money_flow || 'give',
          photos: profile.photos || [],
          description: profile.description || null
        };
      });

      // Transformer les données d'annonces pour correspondre à l'interface SingleSearchResult
      const formattedAnnonceCrushes: SingleSearchResult[] = [];
      
      // Traiter chaque annonce et ajouter seulement les objets valides
      (annoncesData || []).forEach((annonce: any) => {
        const searchResult: SingleSearchResult = {
          id: annonce.id,
          full_name: annonce.nom || 'Utilisateur',
          avatar_url: annonce.photos_urls && annonce.photos_urls.length > 0 ? annonce.photos_urls[0] : '',
          country: annonce.pays || '',
          city: annonce.ville || '',
          gender: annonce.sexe === 'Homme' ? 'homme' : 'femme',
          age: String(annonce.age) || '',
          relation_type: annonce.type_relation === 'court terme' ? 'court' : 'long',
          custom_link_url: annonce.contact || null,
          short_term_intentions: annonce.type_relation === 'court terme' ? 
            mapProfilIntentionsToShortTerm(annonce.intentions_court_terme || []) : null,
          long_term_intentions: annonce.type_relation === 'long terme' ? 
            mapProfilIntentionsToLongTerm(annonce.intentions_long_terme || []) : null,
          money_amount: annonce.prix ? String(annonce.prix) : null,
          money_flow: 'give',
          photos: annonce.photos_urls || [],
          description: annonce.description || null
        };
        formattedAnnonceCrushes.push(searchResult);
      });
      
      // Combiner et retourner les résultats
      const profileAndAnnonceResults = [...mappedProfileResults, ...formattedAnnonceCrushes];
      return profileAndAnnonceResults as SingleSearchResult[];
    } catch (fallbackError) {
      console.error('Fallback method also failed:', fallbackError);
      return [];
    }
  }
}

// Fonction pour mapper les intentions de profil_annonce au format utilisé par SingleSearchResult
function mapProfilIntentionsToShortTerm(intentions: string[]): string[] {
  const mappings: Record<string, string> = {
    'Conclure': 'hookup',
    'Resto': 'resto',
    'Prendre un verre': 'drink',
    'On verra': 'seeWhat',
    'Autres': 'other'
  };
  
  return intentions.map(intention => mappings[intention] || intention.toLowerCase());
}

function mapProfilIntentionsToLongTerm(intentions: string[]): string[] {
  const mappings: Record<string, string> = {
    'Mariage': 'marriage',
    'Enfant': 'children',
    'Fidélité': 'fidelity',
    'Polygamie': 'polygamy',
    'Libre': 'free'
  };
  
  return intentions.map(intention => mappings[intention] || intention.toLowerCase());
}

// Fonction pour incrémenter les clics sur le lien de contact et désactiver la visibilité après 5 clics
export async function incrementContactClicks(profileId: string): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('increment_contact_clicks', { profile_id: profileId });
    
    if (error) {
      console.error('Erreur lors de l\'incrémentation des clics sur le contact:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Exception lors de l\'incrémentation des clics sur le contact:', error);
    return false;
  }
}

// Fonction pour ajouter ou supprimer un crush (like/unlike)
export async function toggleCrush(userId: string, crushId: string, isAnnonce: boolean = false): Promise<{ success: boolean; isLiked: boolean; message?: string }> {
  try {
    // Vérifier si les ID sont valides (au format UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId) || !uuidRegex.test(crushId)) {
      console.error('Format d\'ID invalide pour toggleCrush:', { userId, crushId });
      return { success: false, isLiked: false, message: 'Format d\'ID invalide' };
    }
    
    // Utiliser la table appropriée en fonction du type de profil
    const tableName = isAnnonce ? 'crush_annonces' : 'crushes';
    const idFieldName = isAnnonce ? 'annonce_id' : 'crush_id';
    
    // Vérifier si le crush existe déjà
    const { data: existingCrush, error: checkError } = await supabase
      .from(tableName)
      .select('id')
      .eq('user_id', userId)
      .eq(idFieldName, crushId)
      .maybeSingle();
    
    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = pas de résultat trouvé
      console.error(`Erreur lors de la vérification du crush (${tableName}):`, checkError);
      return { success: false, isLiked: false, message: 'Erreur lors de la vérification' };
    }
    
    // Si le crush existe, le supprimer (unlike)
    if (existingCrush) {
      const { error: deleteError } = await supabase
        .from(tableName)
        .delete()
        .eq('id', existingCrush.id);
      
      if (deleteError) {
        console.error(`Erreur lors de la suppression du crush (${tableName}):`, deleteError);
        return { success: false, isLiked: true, message: 'Erreur lors de la suppression' };
      }
      
      return { success: true, isLiked: false };
    } 
    
    // Plus de limite sur le nombre total de crushs
    // L'ajouter (like)
    const insertData = isAnnonce 
      ? { user_id: userId, annonce_id: crushId }
      : { user_id: userId, crush_id: crushId };
      
    const { error: insertError } = await supabase
      .from(tableName)
      .insert([insertData]);
    
    if (insertError) {
      console.error(`Erreur lors de l'ajout du crush (${tableName}):`, insertError);
      return { success: false, isLiked: false, message: 'Erreur lors de l\'ajout' };
    }
    
    return { success: true, isLiked: true };
    
  } catch (error) {
    console.error('Exception lors du toggle crush:', error);
    return { success: false, isLiked: false, message: 'Exception inattendue' };
  }
}

// Fonction pour récupérer tous les crushes d'un utilisateur (y compris les annonces)
export async function getUserCrushes(userId: string): Promise<Crush[]> {
  try {
    // Vérifier si l'ID est valide (au format UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId)) {
      console.error('Format d\'ID utilisateur invalide pour getUserCrushes:', userId);
      return [];
    }
    
    // 1. Récupérer les crushes de profils normaux
    const { data: profileCrushes, error: profileError } = await supabase
      .from('crushes')
      .select(`
        id,
        user_id,
        crush_id,
        created_at,
        expires_at,
        profiles:crush_id (
          id,
          full_name,
          avatar_url,
          country,
          city,
          gender,
          age,
          relation_type,
          custom_link_url,
          short_term_intentions,
          long_term_intentions,
          money_amount,
          money_flow,
          photos,
          description
        )
      `)
      .eq('user_id', userId)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    
    if (profileError) {
      console.error('Erreur lors de la récupération des crushes de profils:', profileError);
    }
    
    // 2. Récupérer les crushes d'annonces
    const { data: annonceCrushes, error: annonceError } = await supabase
      .from('crush_annonces')
      .select(`
        id,
        user_id,
        annonce_id,
        created_at,
        expires_at,
        profil_annonce:annonce_id (
          id,
          nom,
          sexe,
          age,
          pays,
          ville,
          photos_urls,
          description,
          prix,
          orientation_sexuelle,
          type_relation,
          intentions_court_terme,
          intentions_long_terme,
          contact,
          active
        )
      `)
      .eq('user_id', userId)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    
    if (annonceError) {
      console.error('Erreur lors de la récupération des crushes d\'annonces:', annonceError);
    }
    
    // Transformer les données pour correspondre à l'interface Crush
    const formattedProfileCrushes = (profileCrushes || []).map((crush: any) => ({
      id: crush.id,
      user_id: crush.user_id,
      crush_id: crush.crush_id,
      created_at: crush.created_at,
      expires_at: crush.expires_at,
      is_annonce: false,
      profile: crush.profiles ? {
        id: crush.profiles.id,
        full_name: crush.profiles.full_name || 'Utilisateur',
        avatar_url: crush.profiles.avatar_url || '',
        country: crush.profiles.country || '',
        city: crush.profiles.city || '',
        gender: crush.profiles.gender || '',
        age: crush.profiles.age || '',
        relation_type: crush.profiles.relation_type || '',
        custom_link_url: crush.profiles.custom_link_url,
        short_term_intentions: crush.profiles.short_term_intentions,
        long_term_intentions: crush.profiles.long_term_intentions,
        money_amount: crush.profiles.money_amount || null,
        money_flow: crush.profiles.money_flow || 'give',
        photos: crush.profiles.photos || [],
        description: crush.profiles.description || null
      } : undefined
    }));
    
    // Transformer les données d'annonces pour correspondre à l'interface Crush
    const formattedAnnonceCrushes: Crush[] = [];
    
    // Traiter chaque annonce et ajouter seulement les objets valides
    (annonceCrushes || []).forEach((crush: any) => {
      if (crush.profil_annonce) {
        const annonce = crush.profil_annonce;
        const crushObject: Crush = {
          id: crush.id,
          user_id: crush.user_id,
          crush_id: crush.annonce_id,
          created_at: crush.created_at,
          expires_at: crush.expires_at,
          is_annonce: true,
          profile: {
            id: annonce.id,
            full_name: annonce.nom || 'Utilisateur',
            avatar_url: annonce.photos_urls && annonce.photos_urls.length > 0 ? annonce.photos_urls[0] : '',
            country: annonce.pays || '',
            city: annonce.ville || '',
            gender: annonce.sexe === 'Homme' ? 'homme' : 'femme',
            age: String(annonce.age) || '',
            relation_type: annonce.type_relation === 'court terme' ? 'court' : 'long',
            custom_link_url: annonce.contact || null,
            short_term_intentions: annonce.type_relation === 'court terme' ? 
              mapProfilIntentionsToShortTerm(annonce.intentions_court_terme || []) : null,
            long_term_intentions: annonce.type_relation === 'long terme' ? 
              mapProfilIntentionsToLongTerm(annonce.intentions_long_terme || []) : null,
            money_amount: annonce.prix ? String(annonce.prix) : null,
            money_flow: 'give',
            photos: annonce.photos_urls || [],
            description: annonce.description || null
          }
        };
        formattedAnnonceCrushes.push(crushObject);
      }
    });
    
    // Combiner et retourner les résultats
    const crushResults = [...formattedProfileCrushes, ...formattedAnnonceCrushes];
    return crushResults as Crush[];
    
  } catch (error) {
    console.error('Exception lors de la récupération des crushes:', error);
    return [];
  }
}

// Fonction pour vérifier si un profil est liké par l'utilisateur (profile ou annonce)
export async function isProfileLiked(userId: string, profileId: string, isAnnonce: boolean = false): Promise<boolean> {
  try {
    // Vérifier si les ID sont valides (au format UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId) || !uuidRegex.test(profileId)) {
      console.error('Format d\'ID invalide:', { userId, profileId });
      return false;
    }
    
    // Utiliser la table appropriée en fonction du type de profil
    const tableName = isAnnonce ? 'crush_annonces' : 'crushes';
    const idFieldName = isAnnonce ? 'annonce_id' : 'crush_id';
    
    const { data, error } = await supabase
      .from(tableName)
      .select('id')
      .eq('user_id', userId)
      .eq(idFieldName, profileId)
      .maybeSingle();
    
    if (error) {
      console.error(`Erreur lors de la vérification du like (${tableName}):`, error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error('Exception lors de la vérification du like:', error);
    return false;
  }
}

// Fonction pour compter le nombre total de crushes d'un utilisateur (profils + annonces)
export async function countTotalUserCrushes(userId: string): Promise<number> {
  try {
    // Vérifier si l'ID est valide (au format UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId)) {
      console.error('Format d\'ID utilisateur invalide pour countTotalUserCrushes:', userId);
      return 0;
    }
    
    // Utiliser la fonction SQL qui compte les deux tables
    const { data, error } = await supabase
      .rpc('count_total_user_crushes', { user_id_param: userId });
    
    if (error) {
      console.error('Erreur lors du comptage total des crushes:', error);
      
      // Méthode de secours si la RPC échoue
      const { count: profileCount, error: profileError } = await supabase
        .from('crushes')
        .select('id', { count: 'exact' })
        .eq('user_id', userId)
        .gt('expires_at', new Date().toISOString());
      
      const { count: annonceCount, error: annonceError } = await supabase
        .from('crush_annonces')
        .select('id', { count: 'exact' })
        .eq('user_id', userId)
        .gt('expires_at', new Date().toISOString());
      
      if (profileError || annonceError) {
        console.error('Erreur lors du comptage des crushes (méthode de secours):', 
          { profileError, annonceError });
        return 0;
      }
      
      return (profileCount || 0) + (annonceCount || 0);
    }
    
    return data || 0;
  } catch (error) {
    console.error('Exception lors du comptage des crushes:', error);
    return 0;
  }
}

// Conserver l'ancienne fonction pour la compatibilité
export async function countUserCrushes(userId: string): Promise<number> {
  return countTotalUserCrushes(userId);
} 